import { useCallback, useEffect, useMemo } from "react";
import { useAccountingDispatch, useAccountingState } from "./context";
import { requestPeriodStatus } from "./actions";
import {
  formatPeriodDate,
  getPeriodMinDate,
  isDateInClosedPeriod,
} from "../../Utils/PeriodUtils";

const EMPTY_HISTORY = [];

// Frozen so `isDateClosed` keeps a stable identity while the book is open —
// otherwise every render hands consumers a new function and defeats memoisation.
const NEVER_CLOSED = () => false;

/**
 * usePeriodLock — the single read path for accounting period-lock state.
 *
 * Every surface calls this with the businessID *it* resolved (v1 pages from a
 * query param, v2 from user.mainBusiness.id), so the lock always describes the
 * business actually on screen. Duplicate GETs collapse inside requestPeriodStatus,
 * not here, which is why it is safe for a page, its banner, its modal and one
 * cell per row to all call this.
 *
 * When the business has no closed period every value is inert — `isClosed` false,
 * `minDate` undefined, `isDateClosed` always false — so a book that was never
 * closed produces byte-identical UI to before this feature existed.
 *
 * @param businessID  the business to describe; falsy disables the hook entirely
 * @param options.enabled  set false to skip fetching (e.g. before data resolves)
 * @param options.locale   locale for `closedThroughLabel`; pass i18n.language
 */
export const usePeriodLock = (businessID, options = {}) => {
  const { enabled = true, locale } = options;

  const dispatch = useAccountingDispatch();
  const { periodStatus, periodHistory, periodLoading, periodError } =
    useAccountingState();

  const status = businessID ? periodStatus[businessID] : undefined;
  const history = (businessID && periodHistory[businessID]) || EMPTY_HISTORY;

  useEffect(() => {
    if (!enabled || !businessID) return;
    // undefined means "never fetched" — a fetched-but-open book caches an object
    // with closedThrough: null, so this does not re-request on every mount.
    // Depending on `status` rather than the whole map keeps another business
    // loading from re-triggering this one.
    if (status !== undefined) return;
    requestPeriodStatus(dispatch, { businessID });
  }, [dispatch, enabled, businessID, status]);

  const refetch = useCallback(
    () => requestPeriodStatus(dispatch, { businessID, force: true }),
    [dispatch, businessID],
  );

  const closedThrough = status?.closedThrough || null;

  const isDateClosed = useMemo(() => {
    if (!closedThrough) return NEVER_CLOSED;
    return (date) => isDateInClosedPeriod(date, closedThrough);
  }, [closedThrough]);

  return useMemo(
    () => ({
      status: status || null,
      closedThrough,
      isClosed: Boolean(closedThrough),
      minDate: getPeriodMinDate(closedThrough),
      closedThroughLabel: formatPeriodDate(closedThrough, locale),
      isDateClosed,
      history,
      loading: periodLoading,
      error: periodError,
      refetch,
    }),
    [
      status,
      closedThrough,
      locale,
      isDateClosed,
      history,
      periodLoading,
      periodError,
      refetch,
    ],
  );
};
